import { QueryExpr, QueryField, QueryOperation } from '@apis/Resources';
import { QuerySelectExpr, SchemaField } from '@apis/Resources/model';
import { ActionIcon, Box, createStyles, Group, Popover, Space, Tooltip, useMantineTheme } from '@mantine/core';
import { Filter, Icon, Plus, Trash } from 'tabler-icons-react';
import { FilterItem, LineItemCompactToken } from '@root/Components/Filter/Design';
import { DataFilterModel, DataFilters, ExprToken } from '@root/Components/Filter/Filters';
import { IValueProviderFactory } from '@root/Services/Query/QueryDatasource';
import { IFieldInfoProvider, operationLookup, ParameterInfoFlag, QueryDescriptorService } from '@root/Components/Filter/Services';
import { FieldPicker, FieldPickerItem, FieldPickerTooltip } from '@root/Components/Picker/FieldPicker';
import { FlyoverOverlay } from '@root/Components/Picker/Flyover';
import { EventEmitter, useEvent, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import {
    cleanBoolExpr,
    FieldInfo,
    groupExprs,
    ISupplementalFieldInfo,
    SchemaService,
    sortedObjectId,
    traverseExpr,
    TypeInfo,
} from '@root/Services/QueryExpr';
import React, { forwardRef, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { TooltipWhite } from '@root/Design/Primitives';
import styled from '@emotion/styled';
import { DatasourceSchemaContext, useContextualFilterProps } from '../Filter/DatasourceContext';

export function getUfTokens(expr: QueryExpr | undefined, queryDescriptorSvc: QueryDescriptorService) {
    let tokens = !expr ? [] : queryDescriptorSvc.getTokens(expr);
    tokens = tokens.filter((t) => !t.paramFlags?.includes('filter'));
    const name = !tokens.length ? 'None' : tokens.map((t) => t.text).join(' ');

    return { tokens, name };
}

type ExprDeconstruction = { agg?: string; fieldExpr?: QueryField };
class ExprPickerModel {
    private _fieldAggs = new Map<FieldInfo, FieldInfo[]>();
    private _exprParts: ExprDeconstruction = {};
    private _filters: QueryExpr[] = [];
    private _validFilters: QueryExpr[] = [];
    private operations?: string[];
    private _exprChanged = new EventEmitter<QueryExpr | undefined>(undefined);
    private name: string = '';

    public types?: string[];
    public readonly schemaSvc: SchemaService;
    public readonly queryDescriptorSvc: QueryDescriptorService;
    public readonly dsDescription: { noun: string; nounPlural: string };

    public readonly invalidated = EventEmitter.empty();
    public readonly addFilter = EventEmitter.empty();
    public readonly filterStateChanged = new EventEmitter<{ valid: boolean; empty: boolean }>({ empty: true, valid: true });
    public get exprChanged() {
        return this._exprChanged;
    }

    public get canSetFilter() {
        return !!operationLookup.get(this._exprParts.agg ?? '')?.filterability;
    }

    public get canSetField() {
        return !this._exprParts.agg || !!operationLookup.get(this._exprParts.agg ?? '')?.params?.some((p) => p.flags?.includes('field'));
    }

    public get fieldInfo() {
        return !this.fieldExpr ? undefined : this.schemaSvc.getFieldWithId(this.fieldExpr.Field) ?? this.schemaSvc.getField(this.fieldExpr.Field);
    }

    public get opInfo() {
        return this.queryDescriptorSvc.getOperationInfo(this.agg ?? '');
    }

    public get fieldExpr() {
        return this._exprParts.fieldExpr;
    }

    public get filters() {
        return this._filters;
    }

    public get agg() {
        return this._exprParts.agg;
    }

    public get hasAgg() {
        return !!this._exprParts.agg;
    }

    public get isEmpty() {
        return !this._exprParts.agg && !this._exprParts.fieldExpr;
    }

    public get hasFilter() {
        return !!this.filters.length;
    }

    public get validFilters() {
        return this._validFilters;
    }

    public constructor(schemaCtx: DatasourceSchemaContext) {
        this.schemaSvc = schemaCtx.schemaSvc;
        this.queryDescriptorSvc = schemaCtx.queryDescriptorSvc;
        const { noun = 'record', nounPlural = 'records' } = schemaCtx.datasource;
        this.dsDescription = { noun, nounPlural };
    }

    public getExpr() {
        return this.reconstructExpr();
    }

    public init(expr: undefined | QueryExpr, operations: undefined | ReadonlyArray<string>, types: undefined | string[]) {
        this._exprChanged.emit(expr);
        this.deconstructExpr(expr);
        this.operations = operations?.slice() ?? ['sum', 'avg', 'min', 'max', 'count', 'countuniquevalues', 'percent'];
        this.types = types;
        return this;
    }

    public updateName(name: string) {
        this.name = name;
        this.raiseInvalidated();
    }

    public getSelectedItem() {
        const fieldInfo = this.fieldExpr ? this.schemaSvc.getField(this.fieldExpr.Field) : undefined;
        if (fieldInfo && this.agg) {
            const fieldAggs = this.getOrCreateFieldAggs(fieldInfo);
            const op = this.getFacadeFieldOperation(fieldInfo) ?? '';
            if (op) {
                return fieldAggs?.find((f) => op === this.agg);
            }
        }
        return fieldInfo;
    }

    public raiseAddFilter = () => this.addFilter.emit();

    public getFilterTooltip() {
        const baseExpr = this.getBaseExpr();
        const name = `"${this.name || (!baseExpr ? '' : this.queryDescriptorSvc.getName(baseExpr))}"`;
        const { nounPlural } = this.dsDescription;
        return {
            empty: (
                <>
                    (Optional) Add Filters to calculate <strong>{name}</strong> for only specific {nounPlural}.
                </>
            ),
            valid: (
                <>
                    Filters are applied to calculate <strong>{name}</strong> for only specific {nounPlural}.
                </>
            ),
            invalid: (
                <>
                    Some filters are incomplete and do not yet apply to the <strong>{name}</strong> calculation.{' '}
                </>
            ),
        };
    }

    public transformFieldOptions = (items: FieldPickerItem[]) => {
        let result = items.slice();

        const statsType = {
            Name: FormatService.instance.titleCase(this.dsDescription.noun) + ' Stats',
            Fields: [] as Array<SchemaField & ISupplementalFieldInfo>,
            IsRoot: true,
            TypeId: this.dsDescription.noun,
        };
        if (this.operations?.includes('count')) {
            statsType.Fields!.push({
                Name: `Count of ${this.dsDescription.nounPlural}`,
                TypeName: 'number',
                IsPrimitive: true,
                Expr: { Operation: 'count', Operands: [] },
                Flags: ['expr-facade'],
            });
        }
        if (this.operations?.includes('percent')) {
            statsType.Fields!.push({
                Name: `Percent of ${this.dsDescription.nounPlural}`,
                TypeName: 'number',
                IsPrimitive: true,
                Expr: { Operation: 'percent', Operands: [] },
                Flags: ['expr-facade'],
            });
        }
        if (statsType.Fields.length) {
            const wrapperSchema = SchemaService.create([statsType]);
            result.unshift(wrapperSchema.rootTypeInfo[0]);
        }

        return result;
    };

    public getFieldPickerChildren = (item: FieldInfo | TypeInfo) => {
        if ('path' in item && item.isPrimitive && !item.supplementalInfo.Expr) {
            const result = this.getOrCreateFieldAggs(item);
            return result?.length ? result : undefined;
        } else if ('children' in item) {
            return item.children;
        } else {
            return undefined;
        }
    };

    public setFilters = (filters: QueryExpr[]) => {
        this._filters.splice(0, Infinity, ...filters);
        this.updateValidFilters(filters);
        this.raiseInvalidated();
    };

    public setField = (field: undefined | FieldInfo) => {
        if (field) {
            const agg = this.getFacadeFieldOperation(field);
            const canFilter = !!operationLookup.get(agg ?? '')?.filterability;
            const filterExpr = !canFilter ? undefined : this._filters?.length ? { Operation: 'and', Operands: this._filters } : undefined;
            if (!filterExpr) {
                this._filters?.splice(0, Infinity);
            }
            this.assignExprParts({ fieldExpr: { Field: field.path, Type: field.typeName }, agg });
            this.raiseInvalidated();
        }
    };

    public getUfTokens() {
        const baseExpr = this.getBaseExpr();
        return getUfTokens(baseExpr ?? undefined, this.queryDescriptorSvc);
    }

    private deconstructExpr(expr?: QueryExpr) {
        if (!expr || 'Value' in expr) {
            this.assignExprParts();
        } else if ('Field' in expr) {
            this.assignExprParts({ fieldExpr: expr });
        } else if ('Operation' in expr) {
            this.deconstructOperation(expr);
        }
    }

    private getBaseExpr() {
        const expr = this.reconstructExpr();
        const baseOperation = !expr
            ? null
            : traverseExpr(expr, (x) => {
                  if ('Operation' in x) {
                      const opInfo = operationLookup.get(x.Operation);
                      const isBase = opInfo && opInfo.flags?.includes('agg') && !opInfo.flags?.includes('hidden');
                      return isBase ? x : undefined;
                  }
              });
        const fieldExpr = baseOperation ? null : this.fieldExpr;
        return baseOperation ?? fieldExpr;
    }

    private reconstructExpr() {
        if (!this._exprParts) {
            return undefined;
        }
        const { agg, fieldExpr } = this._exprParts;
        let opInfo = !agg ? undefined : operationLookup.get(agg ?? '');

        if (!opInfo) {
            return fieldExpr;
        }

        let operation = { Operation: agg, Operands: [] } as QueryOperation;
        if (fieldExpr) {
            this.setOperandByFlag(operation, 'field', fieldExpr);
        }

        if (!!this._filters.length) {
            const filterExpr = cleanBoolExpr(groupExprs('and', this._filters)) as QueryExpr;
            const { result, adjustment } = this.queryDescriptorSvc.applyAggFilter(operation, filterExpr);
            if (adjustment !== 'none') {
                operation = result as QueryOperation;
            }
        }

        return operation;
    }

    private deconstructOperation(operation: QueryOperation) {
        const opName = operation.Operation?.toLowerCase();
        const opInfo = operationLookup.get(opName);
        const parts = {} as ExprDeconstruction;

        if (opInfo?.flags?.includes('agg')) {
            const filterExpr = this.getOperandByFlag(operation, 'filter') as QueryOperation;
            this._filters = this.normalizeFilter(filterExpr);
            this.updateValidFilters(this._filters);
            const aggOp = (this.getOperandByFlag(operation, 'value') ?? operation) as QueryOperation;
            parts.agg = aggOp.Operation;
            parts.fieldExpr = this.getOperandByFlag(aggOp, 'field') as QueryField;
        }

        this.assignExprParts(parts);
    }

    private updateValidFilters(filters: QueryExpr[]) {
        this._validFilters = filters.filter(cleanBoolExpr);
    }

    private normalizeFilter(filter?: QueryOperation): QueryExpr[] {
        if (!filter || !('Operation' in filter)) {
            return [];
        } else {
            const operation = filter.Operation?.toLowerCase();
            return operation !== 'and' ? [filter] : (filter.Operands?.slice() as QueryExpr[]) ?? [];
        }
    }

    private getOrCreateFieldAggs(field: FieldInfo) {
        let result = this._fieldAggs.get(field);
        if (!result) {
            this._fieldAggs.set(field, (result = []));
            const fieldAggInfo = this.queryDescriptorSvc.operationInfoProvider.findOperations(this.operations, this.types, undefined, [
                { flag: 'field', type: field.typeName },
            ]);

            this.addValueAgg(field, result);

            if (fieldAggInfo.length) {
                for (const aggInfo of fieldAggInfo) {
                    const expr = { Operation: aggInfo.operation, Operands: [] };
                    this.setOperandByFlag(expr, 'field', { Field: field.path, Type: field.typeName });
                    result.push(
                        field.copy({
                            Name: `${aggInfo.name ?? aggInfo.operation} ${field.name}`,
                            TypeName: aggInfo.type,
                            Expr: expr,
                            Flags: ['expr-facade'],
                        })
                    );
                }
            }
        }
        return result;
    }

    private addValueAgg(field: FieldInfo, collection: FieldInfo[]) {
        if (field.typeName !== 'number' && (!this.types || this.types.includes(field.typeName)) && this.operations?.includes('countuniquevalues')) {
            collection.push(
                field.copy({
                    Expr: { Field: field.path, Type: field.typeName },
                })
            );
        }
    }

    private assignExprParts(parts?: ExprDeconstruction) {
        this._exprParts = parts ?? {};
    }

    private getFacadeFieldOperation(fieldInfo: FieldInfo) {
        return (fieldInfo.supplementalInfo.Expr ?? ({} as QueryOperation))?.Operation;
    }

    private getOperandByFlag(op: QueryOperation, flag: ParameterInfoFlag) {
        return this.queryDescriptorSvc.operationInfoProvider.getOperandByFlag(op, flag);
    }

    private setOperandByFlag(op: QueryOperation, flag: ParameterInfoFlag, value: QueryExpr) {
        this.queryDescriptorSvc.operationInfoProvider.setOperandByFlag(op, flag, value);
    }

    private raiseInvalidated() {
        this.invalidated.emit();
        const currentExpr = this.reconstructExpr();
        const lastEventedExpr = this._exprChanged.value;
        if (!currentExpr || !lastEventedExpr || sortedObjectId(currentExpr) !== sortedObjectId(lastEventedExpr)) {
            this._exprChanged.emit(currentExpr);
        }
    }
}
interface IBaseExprPickerProps<T> extends IInternalExprPickerProps {
    expr?: T;
    onChange: (expr: T) => void;
}
interface ISelectQueryExprPickerProps extends IBaseExprPickerProps<QuerySelectExpr> {
    name?: string;
}
interface IQueryExprPickerProps extends IBaseExprPickerProps<QueryExpr> {}
export function SelectExprPicker(props: ISelectQueryExprPickerProps) {
    const { expr, onChange, name, ...rest } = props;
    const { operations, schemaCtx, types } = rest;
    const model = useMemo(() => new ExprPickerModel(schemaCtx).init(expr?.Expr as QueryExpr, operations, types), [schemaCtx.schemaSvc]);
    useEvent(model.invalidated);
    useEffect(() => model.updateName(name ?? expr?.Alias ?? ''), [expr?.Alias, name]);
    useEffect(() => {
        onChange({ Alias: expr?.Alias, Expr: model.getExpr() });
    }, [JSON.stringify(model.getExpr())]);
    return <InternalExprPicker model={model} {...rest} />;
}
export function ExprPicker(props: IQueryExprPickerProps) {
    const { expr, onChange, ...rest } = props;
    const { operations, schemaCtx, types } = rest;
    const model = useMemo(() => new ExprPickerModel(schemaCtx).init(expr, operations, types), [schemaCtx.schemaSvc]);
    useEvent(model.invalidated);
    useEffect(() => {
        const updatedExpr = model.getExpr();
        if (updatedExpr) {
            onChange(updatedExpr);
        }
    }, [JSON.stringify(model.getExpr())]);
    return <InternalExprPicker model={model} {...rest} />;
}
interface IInternalExprPickerProps {
    schemaCtx: DatasourceSchemaContext;
    onRemove?: () => void;
    removeLabel?: string;
    types?: string[];
    opener?: (open: () => void, defaultOpener: () => ReactNode) => ReactNode;
    operations?: ReadonlyArray<string>;
    rightSection?: ReactNode;
}
function InternalExprPicker(props: IInternalExprPickerProps & { model: ExprPickerModel }) {
    const { model, onRemove, opener, operations, removeLabel, schemaCtx, types } = props;
    const [opened, { open, close }] = useToggle(false);

    const selectedItem = model.getSelectedItem();
    const selection = useMemo(() => (selectedItem ? [selectedItem] : []), [selectedItem]);
    const setSelection = useCallback(
        (selection: FieldInfo[]) => {
            model.setField(selection?.[0]);
            close();
        },
        [model]
    );

    const renderExprFacadeItem = useCallback(
        (item: FieldInfo | TypeInfo, defaultRenderer: () => ReactNode) => {
            if (item instanceof FieldInfo && item.supplementalInfo.Expr) {
                return (
                    <FieldPickerTooltip helpText={item.helpText} position="right">
                        {schemaCtx.queryDescriptorSvc.getTokens(item.supplementalInfo.Expr).map((t, i) => (
                            <>
                                <ExprToken key={i} token={t} inline />
                                &nbsp;
                            </>
                        ))}
                    </FieldPickerTooltip>
                );
            }
            return defaultRenderer();
        },
        [schemaCtx.queryDescriptorSvc]
    );

    const defaultOpener = () => <ExprButton model={model} onClick={open} active={opened} />;
    return (
        <>
            <Group spacing={3} noWrap>
                <ExprAddFilterButton model={model} />
                <FlyoverOverlay opened={opened} onClick={close} routeBound />
                <Popover zIndex={1000} opened={opened} onClose={close} position="top" withinPortal shadow="lg" offset={0} radius="md" withArrow>
                    <Popover.Target>{opener ? opener(open, defaultOpener) : defaultOpener()}</Popover.Target>
                    <Popover.Dropdown p={0} sx={{ border: `solid 1px #0003` }}>
                        <Box sx={{ height: 400 }}>
                            <FieldPicker
                                schema={schemaCtx.schemaSvc}
                                mode="single"
                                types={model.types}
                                selections={selection}
                                transformItems={model.transformFieldOptions}
                                getChildren={model.getFieldPickerChildren}
                                onChange={setSelection}
                                renderItem={renderExprFacadeItem}
                                tooltipPosition="right"
                            />
                        </Box>
                    </Popover.Dropdown>
                </Popover>
                {props.rightSection}
                {onRemove ? (
                    <Tooltip label={removeLabel}>
                        <ActionIcon onClick={onRemove}>
                            <Trash size={16} />
                        </ActionIcon>
                    </Tooltip>
                ) : null}
            </Group>
            <ExprPickerFilter schemaCtx={schemaCtx} pickerModel={model} />
        </>
    );
}

interface IExprButtonProps {
    model: ExprPickerModel;
    onClick: () => void;
    active: boolean;
}
const ExprButton = forwardRef<HTMLDivElement, IExprButtonProps>(function ExprButton(props, ref) {
    const { model, onClick, active, ...rest } = props;
    useEvent(model.invalidated);
    const { tokens } = model.getUfTokens();

    return (
        <ExprButtonEl ref={ref} onClick={onClick} state={active ? 'active' : model.isEmpty ? 'invalid' : 'valid'} {...rest}>
            {!tokens.length ? (
                <ExprToken token={{ text: 'Select', type: 'none', expr: {}, name: 'Select' }} />
            ) : (
                tokens.map((t, i) => <ExprToken key={i} token={t} />)
            )}
        </ExprButtonEl>
    );
});

export const ExprButtonEl = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof FilterItem>>(function ExprButton(props, ref) {
    const { children, ...rest } = props;
    return (
        <FilterItem ref={ref} mode="button" corners={4} {...rest}>
            {children}
        </FilterItem>
    );
});

function ExprAddFilterButton({ model }: { model: ExprPickerModel }) {
    const { valid, empty } = useEventValue(model.filterStateChanged)!;

    const tooltips = model.getFilterTooltip();
    const tooltip = !valid ? tooltips.invalid : empty ? tooltips.empty : tooltips.valid;

    return !model.canSetFilter ? null : (
        <ExprPickerAdornment
            color={empty ? 'success' : 'gray'}
            tooltip={tooltip}
            icon={Filter}
            onClick={() => (empty ? model.raiseAddFilter() : null)}
            pointDown={!empty}
        />
    );
}

interface IExprPickerAdornmentProps {
    icon: Icon;
    tooltip: ReactNode;
    color: 'success' | 'primary' | 'gray' | 'error' | 'warning';
    pointDown?: boolean;
    onClick?: () => void;
}
export function ExprPickerAdornment(props: IExprPickerAdornmentProps) {
    const { color, icon: Icon, onClick, tooltip, ...rest } = props;
    const { colors } = useMantineTheme();

    const iconColor = colors[color][6];
    const { classes } = useExprAdornmentStyles({ color: colors[color][4], pointDown: rest.pointDown });

    return (
        <TooltipWhite multiline withinPortal position="top" withArrow label={tooltip}>
            <ActionIcon className={classes.button} size={30} variant="outline" onClick={onClick}>
                <Icon stroke={iconColor} size={16} />
            </ActionIcon>
        </TooltipWhite>
    );
}
const useExprAdornmentStyles = createStyles((theme, params: { color: string; pointDown?: boolean }) => ({
    button: {
        borderColor: params.color,
        ['&::after']: {
            content: '""',
            display: !params.pointDown ? 'none' : 'block',
            background: theme.white,
            width: 10,
            height: 10,
            position: 'absolute',
            transform: `translate(0px, 15px) rotate(45deg)`,
            borderStyle: 'solid',
            borderColor: params.color,
            borderWidth: !params.pointDown ? '0' : '0 1px 1px 0',
        },
    },
}));

function ExprPickerFilter(props: { schemaCtx: DatasourceSchemaContext; pickerModel: ExprPickerModel }) {
    const { schemaCtx, pickerModel } = props;
    const {
        valueProvider,
        queryDescriptorSvc: { fieldInfoProvider },
    } = schemaCtx;
    const { filters, canSetFilter, setFilters: onChange, addFilter, schemaSvc, filterStateChanged } = pickerModel;
    const onStateChange = filterStateChanged.emit;
    const { empty, valid } = useEventValue(filterStateChanged)!;
    const canAdd = pickerModel.validFilters.length > 0 && valid;

    return !canSetFilter ? (
        <></>
    ) : (
        <ExprPickerFilterContainer empty={empty}>
            <ExprFilter {...{ filters, onChange, onStateChange, valueProvider, fieldInfoProvider, addFilter, schemaSvc, canAdd }} />
        </ExprPickerFilterContainer>
    );
}
const ExprPickerFilterContainer = styled.div<{ empty: boolean }>`
    border: solid ${(p) => (p.empty ? 0 : 1)}px ${(p) => p.theme.colors.gray[4]};
    border-radius: ${(p) => p.theme.radius.sm}px;
    padding-left: 6px;
    margin-bottom: ${(p) => (p.empty ? 0 : 16)}px;
    justify-self: stretch;
`;

interface ExprFilterProps {
    filters: QueryExpr[];
    onChange: (filters: QueryExpr[]) => void;
    onStateChange: (state: { valid: boolean; empty: boolean }) => void;
    fieldInfoProvider: IFieldInfoProvider;
    valueProvider: IValueProviderFactory;
    schemaSvc: SchemaService;
    addFilter: EventEmitter<void>;
    canAdd?: boolean;
}
function ExprFilter(props: ExprFilterProps) {
    const { filters, onChange, onStateChange, fieldInfoProvider, valueProvider, addFilter, schemaSvc, canAdd } = props;
    const [filterModel, setFilterModel] = useState<DataFilterModel>();
    const fieldPickerRenderer = useCallback((select: (field: string) => void) => {
        return <FieldPicker mode="single" selections={[]} schema={schemaSvc} onChange={(fields) => select(fields[0].path)} />;
    }, []);
    const handleAddFilter = useCallback(() => filterModel?.addEmptyFilter(true, true), [filterModel]);
    useEvent(addFilter, handleAddFilter);
    const handleStateChange = useCallback(
        () => onStateChange({ valid: !filterModel?.getValidationIssues().length, empty: !filterModel?.filters.length }),
        [filterModel, onStateChange]
    );
    useEffect(handleStateChange, [filterModel]);
    useEvent(filterModel?.filtersChanged, handleStateChange);

    const filterProps = useContextualFilterProps();

    return (
        <>
            <DataFilters
                dataFiltersAsLineItem
                lineItemCompact
                onModelLoaded={setFilterModel}
                fieldInfoProvider={fieldInfoProvider}
                align="end"
                renderFieldPicker={fieldPickerRenderer}
                filters={filters}
                showErrors
                onChange={onChange}
                valueProvider={valueProvider}
                {...filterProps}
            />
            {!canAdd ? null : (
                <>
                    <LineItemCompactToken onClick={handleAddFilter}>
                        <Group spacing={4}>
                            <Plus size={16} /> Add another filter
                        </Group>
                    </LineItemCompactToken>
                    <Space h={4} />
                </>
            )}
        </>
    );
}
